import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { DuplicateGroup, ArchiveList, LostEntries } from '@/components/planning/IntegrityParts';
import { LegacyHoursReview } from '@/components/planning/LegacyHoursReview';
import { PhotoEvidenceReview } from '@/components/planning/PhotoEvidenceReview';
import { DateFieldFr } from '@/components/ui/date-field-fr';
import { PurgedDaysReview } from '@/components/planning/PurgedDaysReview';

const isSA = (u) => u?.is_super_admin === true || u?.email === 'transport@dlv-express.com';
const err = (e) => e?.response?.data?.detail || 'Erreur réseau';

export default function AdminPlanningIntegrity({ user }) {
  const [dups, setDups] = useState(null);
  const [archive, setArchive] = useState([]);
  const [lost, setLost] = useState(null);
  const [range, setRange] = useState(() => {
    const end = new Date(); const start = new Date(); start.setDate(end.getDate() - 60);
    const f = (d) => d.toISOString().slice(0, 10);
    return { start: f(start), end: f(new Date(end.getTime() + 60 * 86400000)) };
  });

  const load = useCallback(async () => {
    try {
      const [d, a] = await Promise.all([
        axios.get(`${API}/schedule/integrity/duplicates`),
        axios.get(`${API}/schedule/integrity/archive`),
      ]);
      setDups(d.data.groups); setArchive(a.data.records);
    } catch (e) { toast.error(err(e)); }
  }, []);
  useEffect(() => { if (isSA(user)) load(); }, [user, load]);

  const resolve = async (g, keepId) => {
    if (!window.confirm('Conserver cette version ? Les autres seront archivées (récupérables).')) return;
    try {
      await axios.post(`${API}/schedule/integrity/duplicates/resolve`, { user_id: g.user_id, date: g.date, keep_schedule_id: keepId });
      toast.success('Doublon résolu, autres versions archivées'); load();
    } catch (e) { toast.error(err(e)); load(); }
  };
  const restore = async (rec) => {
    try {
      const cur = await axios.get(`${API}/schedule/integrity/cell-history?user_id=${rec.user_id}&date=${rec.date}`);
      const current = [...cur.data.current_rows].sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)))[0];
      if (!window.confirm('Restaurer cette version ? La version actuelle sera archivée (récupérable).')) return;
      await axios.post(`${API}/schedule/integrity/archive/${rec.archive_id}/restore`, { current_updated_at: current?.updated_at || null });
      toast.success('Version restaurée'); load();
    } catch (e) { toast.error(err(e)); }
  };
  const searchLost = async () => {
    try {
      const r = await axios.get(`${API}/schedule/integrity/lost-entries?start_date=${range.start}&end_date=${range.end}`);
      setLost(r.data.entries);
    } catch (e) { toast.error(err(e)); }
  };

  if (!isSA(user)) return <Layout user={user} activeModule="b2b"><p className="p-6" data-testid="integrity-forbidden">Réservé au Super Admin.</p></Layout>;
  return (
    <Layout user={user} activeModule="b2b">
      <div className="mx-auto max-w-6xl space-y-8 p-4" data-testid="planning-integrity-page">
        <header>
          <h1 className="text-2xl font-bold text-slate-900">Planning — doublons et récupération</h1>
          <p className="text-sm text-slate-600">Aucune donnée n’est supprimée : toute version écartée est archivée et peut être restaurée.</p>
          <a href="/admin/planning/recovery-guide" className="mt-1 inline-block text-sm font-medium text-blue-700 underline" data-testid="recovery-guide-link">Guide de récupération des journées supprimées et message au support</a>
        </header>
        <section>
          <h2 className="mb-2 text-lg font-semibold">Journées en double ({dups ? dups.length : '…'})</h2>
          {dups?.length === 0 && <p className="text-sm text-emerald-700" data-testid="integrity-no-duplicates">Aucun doublon : une seule journée par salarié et par date.</p>}
          <div className="space-y-4">{(dups || []).map((g) => <DuplicateGroup key={`${g.user_id}|${g.date}`} group={g} onKeep={resolve} />)}</div>
        </section>
        <section>
          <h2 className="mb-2 text-lg font-semibold">Recherche de saisies disparues</h2>
          <p className="mb-2 text-sm text-slate-600">Liste les enregistrements confirmés par le serveur dont la valeur n’est plus celle affichée aujourd’hui (modifiée ensuite ou ligne absente).</p>
          <div className="flex flex-wrap items-end gap-2">
            <DateFieldFr value={range.start} onChange={(v) => setRange({ ...range, start: v })} testid="lost-start" />
            <DateFieldFr value={range.end} onChange={(v) => setRange({ ...range, end: v })} testid="lost-end" />
            <Button onClick={searchLost} data-testid="lost-search-btn">Rechercher</Button>
          </div>
          {lost && <LostEntries entries={lost} />}
        </section>
        <PurgedDaysReview />
        <PhotoEvidenceReview />
        <LegacyHoursReview />
        <section>
          <h2 className="mb-2 text-lg font-semibold">Versions archivées ({archive.length})</h2>
          <ArchiveList records={archive} onRestore={restore} />
        </section>
      </div>
    </Layout>
  );
}
